import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const [BLUE, ORANGE] = CHART_COLORS;

/** Arrowhead shared by both drawings. */
function ArrowMarker({ id, color }: { id: string; color: string }) {
  return (
    <marker
      id={id}
      markerHeight="8"
      markerWidth="8"
      orient="auto-start-reverse"
      refX="7"
      refY="4"
    >
      <path d="M0,0 L8,4 L0,8 z" fill={color} />
    </marker>
  );
}

function Label({
  x,
  y,
  children,
  color = CHART_INK.text,
  anchor = "middle",
}: {
  x: number;
  y: number;
  children: string;
  color?: string;
  anchor?: "start" | "middle" | "end";
}) {
  return (
    <text
      fill={color}
      fontSize="15"
      fontStyle="italic"
      textAnchor={anchor}
      x={x}
      y={y}
    >
      {children}
    </text>
  );
}

/** Two bodies pulling on each other with equal and opposite forces, r apart. */
export function ForceDiagram({
  id,
  label,
  description,
  m1,
  m2,
}: {
  id: string;
  label: string;
  description: string;
  m1: number;
  m2: number;
}) {
  const share = m1 / (m1 + m2);
  const r1 = 14 + 40 * Math.sqrt(share);
  const r2 = 14 + 40 * Math.sqrt(1 - share);
  const x1 = 120;
  const x2 = 440;
  const y = 90;
  const arrow = `${id}-arrow`;
  return (
    <ChartSvg
      description={description}
      height={190}
      id={id}
      label={label}
      width={560}
    >
      <defs>
        <ArrowMarker color={CHART_INK.text} id={arrow} />
      </defs>
      <circle cx={x1} cy={y} fill={BLUE} fillOpacity="0.85" r={r1} />
      <circle cx={x2} cy={y} fill={ORANGE} fillOpacity="0.85" r={r2} />
      <Label x={x1} y={y + 5}>
        m₁
      </Label>
      <Label x={x2} y={y + 5}>
        m₂
      </Label>
      <line
        markerEnd={`url(#${arrow})`}
        stroke={CHART_INK.text}
        strokeWidth="2.5"
        x1={x1 + r1 + 6}
        x2={x1 + r1 + 66}
        y1={y}
        y2={y}
      />
      <line
        markerEnd={`url(#${arrow})`}
        stroke={CHART_INK.text}
        strokeWidth="2.5"
        x1={x2 - r2 - 6}
        x2={x2 - r2 - 66}
        y1={y}
        y2={y}
      />
      <Label x={x1 + r1 + 36} y={y - 10}>
        F
      </Label>
      <Label x={x2 - r2 - 36} y={y - 10}>
        F
      </Label>
      <line
        markerEnd={`url(#${arrow})`}
        markerStart={`url(#${arrow})`}
        stroke={CHART_INK.muted}
        strokeWidth="1.5"
        x1={x1}
        x2={x2}
        y1={158}
        y2={158}
      />
      <line
        stroke={CHART_INK.axis}
        strokeDasharray="3 3"
        x1={x1}
        x2={x1}
        y1={y + r1}
        y2={164}
      />
      <line
        stroke={CHART_INK.axis}
        strokeDasharray="3 3"
        x1={x2}
        x2={x2}
        y1={y + r2}
        y2={164}
      />
      <Label x={(x1 + x2) / 2} y={150}>
        r
      </Label>
    </ChartSvg>
  );
}

/** A body, a point at height h above its surface and the field arrow g pointing in. */
export function FieldDiagram({
  id,
  label,
  description,
  heightRatio,
}: {
  id: string;
  label: string;
  description: string;
  /** Altitude as a fraction of the body's radius. */
  heightRatio: number;
}) {
  const cx = 140;
  const cy = 100;
  const radius = 62;
  const rise = Math.min(Math.max(heightRatio * radius, 28), 240);
  const px = cx + radius + rise;
  const arrow = `${id}-arrow`;
  return (
    <ChartSvg
      description={description}
      height={200}
      id={id}
      label={label}
      width={560}
    >
      <defs>
        <ArrowMarker color={CHART_INK.text} id={arrow} />
      </defs>
      <circle cx={cx} cy={cy} fill={BLUE} fillOpacity="0.85" r={radius} />
      <line
        stroke={CHART_INK.axis}
        strokeDasharray="4 3"
        x1={cx}
        x2={px}
        y1={cy}
        y2={cy}
      />
      <circle cx={px} cy={cy} fill={ORANGE} r={7} />
      <line
        markerEnd={`url(#${arrow})`}
        stroke={CHART_INK.text}
        strokeWidth="2.5"
        x1={px - 10}
        x2={px - 62}
        y1={cy}
        y2={cy}
      />
      <Label x={px - 36} y={cy - 12}>
        g
      </Label>
      <Label color="#ffffff" x={cx} y={cy + 5}>
        M
      </Label>
      <line
        markerEnd={`url(#${arrow})`}
        markerStart={`url(#${arrow})`}
        stroke={CHART_INK.muted}
        strokeWidth="1.5"
        x1={cx}
        x2={cx + radius - 3}
        y1={cy + 40}
        y2={cy + 40}
      />
      <Label x={cx + radius / 2} y={cy + 56}>
        R
      </Label>
      <line
        markerEnd={`url(#${arrow})`}
        markerStart={`url(#${arrow})`}
        stroke={CHART_INK.muted}
        strokeWidth="1.5"
        x1={cx + radius + 3}
        x2={px}
        y1={cy + 40}
        y2={cy + 40}
      />
      <Label x={(cx + radius + px) / 2} y={cy + 56}>
        h
      </Label>
    </ChartSvg>
  );
}
